import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, parseISO, isValid as isValidDate } from "date-fns";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import DatePicker from "@/components/common/date-picker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useCreateProfessional } from "../hooks/use-create-professional";
import { Professional } from "@/pages/professionals/types/professional";
import {
  professionalSchema,
  ProfessionalFormData,
  ProfessionalType,
  JobCommitment,
  professionalTypeLabels,
  jobCommitmentLabels,
} from "@/features/professionals/schema/professional-schema";
import { PhoneNumberInput } from "@/components/common/phone-number-input";
import {
  DEFAULT_COUNTRY,
  resolvePhone,
  toE164,
  type CountryCode,
} from "@/utils/phone";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "@/hooks/use-toast";
import { CreateProfessionalInput } from "../services/create-professional";
import MultiSelectTags from "./multi-select-tags";
import { useUpdateProfessional } from "../hooks/use-update-professional";
import FileUploadField from "./file-upload";
import LanguageTagsInput from "./language-selector";
import ProfessionalLeadsSelector from "./professional-leads-selector";
import {
  LeadOption,
  SupervisorOption,
} from "../hooks/use-professionals";
import ProfessionalStatusPanel from "./professional-status-panel";
import ProfessionalDashboardInvitePanel from "./professional-dashboard-invite-panel";
import { useUpdateFeatureFlags } from "../hooks/use-update-feature-flags";

const DEPARTMENTS = [
  "Therapy",
  "Psychiatry",
  "Women's Health",
  "Men's Sexual Health",
];

const ASSOCIATED_TEAMS: { value: string; label: string }[] = [
  { value: "SEXUAL_HEALTH_TEAM", label: "Sexual Health Team" },
];
const SEX_OPTIONS = ["F", "M"];
const PRONOUNS = ["They/Them", "He/Him", "She/Her"];

const MIN_DOB_YEAR = 1920;

type AddEditProfessionalsProps = {
  onClose: () => void;
  initialData?: Professional | null;
  editMode?: boolean;
  /**
   * Show the professional without letting them be changed: fields disabled,
   * the footer reduced to Close. For viewers with View but not Edit on Supply
   * Config — the drawer still opens, it just cannot save.
   */
  readOnly?: boolean;
  onDelete?: (id: string) => void;
  supervisors: SupervisorOption[];
  leads: LeadOption[];
};

export default function AddEditProfessionalsForm({
  onClose,
  initialData,
  editMode,
  readOnly = false,
  onDelete,
  supervisors,
  leads,
}: AddEditProfessionalsProps) {
  const createProfessional = useCreateProfessional();
  const updateProfessional = useUpdateProfessional();
  const updateFeatureFlags = useUpdateFeatureFlags();

  // Stored numbers are one international string; the form shows each as a
  // country + digits pair and recombines on submit (RH-856).
  const initialPhone = resolvePhone(initialData?.phone_number);
  const initialAltPhone = resolvePhone(
    initialData?.preferred_notification_number,
  );

  const form = useForm<ProfessionalFormData>({
    resolver: zodResolver(professionalSchema),
    mode: "onChange",
    defaultValues: {
      name: initialData?.name || "",
      type: initialData?.type || ProfessionalType.COUNSELING_PSYCHOLOGIST,
      sex: initialData?.sex || "",
      date_of_birth: initialData?.date_of_birth ?? null,
      email: initialData?.email || null,
      phone_country_iso: initialPhone.iso ?? DEFAULT_COUNTRY,
      phone_number: initialPhone.national || null,
      registration_number: initialData?.registration_number || null,
      years_of_experience: initialData?.years_of_experience || null,
      personal_email: initialData?.personal_email || null,
      address: initialData?.address || null,
      area_of_expertise: initialData?.area_of_expertise || [],
      associated_teams: initialData?.associated_teams || [],
      languages: initialData?.languages || [],
      department: initialData?.department || "",
      age_range_minor: initialData?.age_range_minor || [0, 17],
      age_range_adult: initialData?.age_range_adult || [18, 100],
      bio: initialData?.bio || null,
      country: initialData?.country || null,
      has_kid: initialData?.has_kid || null,
      is_lead: initialData?.is_lead || null,
      is_married: initialData?.is_married || null,
      is_supervisor: initialData?.is_supervisor || null,
      qualification: initialData?.qualification || [],
      specialization: initialData?.specialization || null,
      university_name: initialData?.university_name || null,
      preferred_notification_country_iso:
        initialAltPhone.iso ?? DEFAULT_COUNTRY,
      preferred_notification_number: initialAltPhone.national || null,
      pronouns: initialData?.pronouns || null,
      supervisor_id: initialData?.supervisor_id || null,
      lead_ids: initialData?.leads?.map((lead) => lead.id) || [],
      job_commitment: initialData?.job_commitment || JobCommitment.FULL_TIME,
      picture: null,
      signature: null,
      stamp: null,
      stamp_and_signature: null,
      professional_image: null,
    },
  });

  const { control } = form;

  const parseDOBToDate = (value: string | null): Date | undefined => {
    if (!value) return undefined;
    const parsed = parseISO(value);
    return isValidDate(parsed) ? parsed : undefined;
  };

  const onSubmit = async (data: ProfessionalFormData) => {
    try {
      // The country fields are form-only; the API takes the combined number.
      const {
        phone_country_iso,
        preferred_notification_country_iso,
        ...rest
      } = data;
      const mutationData: CreateProfessionalInput = {
        ...rest,
        name: data.name.trim(),
        type: data.type,
        sex: data.sex.trim(),
        job_commitment: data.job_commitment || JobCommitment.FULL_TIME,

        // Arrays with defaults
        age_range_minor: data.age_range_minor || [0, 17],
        age_range_adult: data.age_range_adult || [18, 100],
        languages: data.languages || [],

        date_of_birth: data.date_of_birth ?? undefined,
        years_of_experience: data.years_of_experience ?? null,

        // Booleans with false defaults
        is_supervisor: Boolean(data.is_supervisor),
        is_lead: Boolean(data.is_lead),
        has_kid: Boolean(data.has_kid),
        is_married: Boolean(data.is_married),

        // Optional strings
        email: data.email || undefined,
        phone_number:
          toE164(data.phone_number, phone_country_iso as CountryCode) ||
          undefined,
        registration_number: data.registration_number || undefined,
        personal_email: data.personal_email || undefined,
        address: data.address || undefined,
        area_of_expertise: data.area_of_expertise || [],
        associated_teams: data.associated_teams || [],
        bio: data.bio || undefined,
        country: data.country || undefined,
        qualification: data.qualification || [],
        specialization: data.specialization || undefined,
        university_name: data.university_name || undefined,
        preferred_notification_number:
          toE164(
            data.preferred_notification_number,
            preferred_notification_country_iso as CountryCode,
          ) || undefined,
        pronouns: data.pronouns || undefined,
        supervisor_id: data.supervisor_id || undefined,
        lead_ids: data.lead_ids || [],
        department: data.department || undefined,
      };

      if (editMode && initialData?.id) {
        await updateProfessional.mutateAsync({
          id: initialData.id,
          data: mutationData,
        });
      } else {
        await createProfessional.mutateAsync(mutationData);
      }
      toast({
        title: "Success",
        description: "Professional created successfully",
      });
      onClose();
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Error",
        description: "Failed to create professional",
        variant: "destructive",
      });
    }
  };

  const {
    formState: { errors, isSubmitting },
  } = form;

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit, (errors) => {
          toast({
            title: "Validation Error",
            description: "Please check the form for errors",
            variant: "destructive",
          });
        })}
        className="bg-white h-fit pb-8 rounded-lg"
      >
        {/* error summary at the top - debugging ....  */}
        {Object.keys(errors).length > 0 && (
          <div className="p-4 mb-4 bg-red-50 text-red-500">
            <h5>Please fix the following errors:</h5>
            <ul>
              {Object.entries(errors).map(([key, error]) => (
                <li key={key}>
                  {key}: {error?.message ? String(error.message) : ""}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="bg-sidebar rounded h-16 flex items-center px-8">
          <h4 className="text-xl text-white">
            {readOnly ? "" : editMode ? "Edit " : "Add "}
            Professional
          </h4>
        </div>

        {editMode && initialData && (
          <ProfessionalStatusPanel
            professionalId={initialData.id}
            professionalName={initialData.name}
            initialStatus={initialData.status}
          />
        )}

        {editMode && initialData && (
          <ProfessionalDashboardInvitePanel
            professionalId={initialData.id}
            email={initialData.email}
            status={initialData.status}
            clerkId={initialData.clerk_id}
          />
        )}

        <div className="py-5 px-6 grid grid-cols-1 gap-y-7">
          <fieldset
            disabled={readOnly}
            className="grid grid-cols-1 gap-y-7 min-w-0"
          >
          {/* Basic Information */}
          <div>
            <h4>Basic Information</h4>
            <div className="mt-3 grid grid-cols-2 gap-x-2 gap-y-4">
              <FormField
                control={control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Full Name"
                        className="shadow-none drop-shadow-none h-12"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="h-12">
                          <SelectValue placeholder="Select Type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(professionalTypeLabels).map(
                          ([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          )
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="phone_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number</FormLabel>
                    <FormControl>
                      <PhoneNumberInput
                        value={{
                          iso: form.watch("phone_country_iso") as CountryCode,
                          national: field.value || "",
                        }}
                        onChange={({ iso, national }) => {
                          form.setValue("phone_country_iso", iso, {
                            shouldValidate: true,
                          });
                          field.onChange(national || null);
                        }}
                        onBlur={field.onBlur}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Professional Email"
                        type="email"
                        className="shadow-none drop-shadow-none h-12"
                        {...field}
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value || null)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="mt-4">
              {/* languages dropdown */}
              <FormField
                control={control}
                name="languages"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Languages</FormLabel>
                    <FormControl>
                      <LanguageTagsInput
                        value={field.value || []}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Professional Details */}
          <div className="mt-3">
            <h4>Professional Details</h4>
            <div className="mt-3 grid grid-cols-2 gap-x-2 gap-y-4">
              <FormField
                control={control}
                name="department"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Department</FormLabel>
                    <Select
                      value={field.value || ""}
                      onValueChange={field.onChange}
                    >
                      <FormControl>
                        <SelectTrigger className="h-12">
                          <SelectValue placeholder="Select Department" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {DEPARTMENTS.map((dept) => (
                          <SelectItem key={dept} value={dept}>
                            {dept}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="associated_teams"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Associated Teams</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <button
                            type="button"
                            className="flex h-12 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                          >
                            <span className="truncate text-left">
                              {(field.value || []).length === 0
                                ? "Select teams"
                                : ASSOCIATED_TEAMS.filter((t) =>
                                    (field.value || []).includes(t.value),
                                  )
                                    .map((t) => t.label)
                                    .join(", ")}
                            </span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="ml-2 flex-shrink-0 opacity-50"
                            >
                              <path d="m6 9 6 6 6-6" />
                            </svg>
                          </button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-[220px] p-2" align="start">
                        <div className="space-y-1">
                          {ASSOCIATED_TEAMS.map((team) => {
                            const selected = (field.value || []).includes(
                              team.value,
                            );
                            return (
                              <label
                                key={team.value}
                                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent"
                              >
                                <Checkbox
                                  checked={selected}
                                  onCheckedChange={(checked) => {
                                    const current = field.value || [];
                                    field.onChange(
                                      checked
                                        ? [...current, team.value]
                                        : current.filter(
                                            (v: string) => v !== team.value,
                                          ),
                                    );
                                  }}
                                />
                                {team.label}
                              </label>
                            );
                          })}
                        </div>
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="specialization"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Qualification</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Qualification"
                        className="shadow-none drop-shadow-none h-12"
                        {...field}
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value || null)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="years_of_experience"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Experience in years</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Years of Experience"
                        type="number"
                        min={0}
                        max={100}
                        className="shadow-none drop-shadow-none h-12"
                        {...field}
                        value={field.value || ""}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value ? parseInt(e.target.value) : null
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="university_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>University Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="University Name"
                        className="shadow-none drop-shadow-none h-12"
                        {...field}
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value || null)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="registration_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Registration Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Registration Number"
                        className="shadow-none drop-shadow-none h-12"
                        {...field}
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value || null)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="area_of_expertise"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Area of Expertise</FormLabel>
                    <FormControl>
                      <MultiSelectTags
                        value={field.value || []}
                        onChange={(values) => field.onChange(values)}
                        placeholder="Press Enter or add comma to add"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Personal Details */}
          <div className="mt-3">
            <h4>Personal Details</h4>
            <div className="mt-3 grid grid-cols-2 gap-x-2 gap-y-4">
              <FormField
                control={control}
                name="sex"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sex</FormLabel>
                    <Select
                      value={field.value || ""}
                      onValueChange={field.onChange}
                    >
                      <FormControl>
                        <SelectTrigger className="h-12">
                          <SelectValue placeholder="Select Sex" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {SEX_OPTIONS.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="date_of_birth"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date of birth</FormLabel>
                    <FormControl>
                      <DatePicker
                        value={field.value}
                        onChange={(date) =>
                          field.onChange(
                            date ? format(date, "yyyy-MM-dd") : null,
                          )
                        }
                        placeholder="Pick date of birth"
                        fromYear={MIN_DOB_YEAR}
                        toYear={new Date().getFullYear()}
                        defaultMonth={
                          parseDOBToDate(field.value) ?? new Date(1990, 0, 1)
                        }
                        disabled={(date) => date > new Date()}
                        className="h-12"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Country"
                        className="shadow-none drop-shadow-none h-12"
                        {...field}
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value || null)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Address"
                        className="shadow-none drop-shadow-none h-12"
                        {...field}
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value || null)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="pronouns"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pronouns</FormLabel>
                    <Select
                      value={field.value || ""}
                      onValueChange={field.onChange}
                    >
                      <FormControl>
                        <SelectTrigger className="h-12">
                          <SelectValue placeholder="Select Pronouns" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {PRONOUNS.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <br />
              <div className="flex items-center gap-x-6 mt-3">
                <FormField
                  control={control}
                  name="is_married"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-x-2">
                        <FormControl>
                          <Checkbox
                            checked={field.value || false}
                            onCheckedChange={field.onChange}
                            className="peer bg-transparent data-[state=checked]:bg-sidebar data-[state=checked]:border-transparent [&>span>svg]:fill-white [&>span>svg]:text-white"
                          />
                        </FormControl>
                        <label className="text-sm font-medium">Married</label>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="has_kid"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-x-2">
                        <FormControl>
                          <Checkbox
                            checked={field.value || false}
                            onCheckedChange={field.onChange}
                            className="peer bg-transparent data-[state=checked]:bg-sidebar data-[state=checked]:border-transparent [&>span>svg]:fill-white [&>span>svg]:text-white"
                          />
                        </FormControl>
                        <label className="text-sm font-medium">Has Kids</label>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>

          {/* Role and Commitment */}
          <div className="mt-3">
            <h4>Role &amp; Commitment</h4>
            <div className="mt-3">
              <div className="flex gap-x-3 gap-y-4">
                <FormField
                  control={control}
                  name="job_commitment"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Job Commitment</FormLabel>
                      <Select
                        value={field.value ?? JobCommitment.FULL_TIME}
                        onValueChange={field.onChange}
                      >
                        <FormControl>
                          <SelectTrigger className="w-[280px] drop-shadow-none shadow-none">
                            <SelectValue placeholder="Job Commitment" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Object.entries(jobCommitmentLabels).map(
                            ([value, label]) => (
                              <SelectItem key={value} value={value}>
                                {label}
                              </SelectItem>
                            )
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* toggle fields  */}
              <div className="flex items-center gap-x-8 mt-6">
                <FormField
                  control={control}
                  name="is_supervisor"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2">
                        <FormControl>
                          <Switch
                            checked={field.value || false}
                            onCheckedChange={field.onChange}
                            className="data-[state=checked]:bg-sidebar data-[state=checked]:border-sidebar"
                          />
                        </FormControl>
                        <label className="text-sm font-medium">
                          Supervisor
                        </label>
                      </div>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="is_lead"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2">
                      <FormControl>
                        <Switch
                          checked={field.value || false}
                          onCheckedChange={field.onChange}
                          className="data-[state=checked]:bg-sidebar data-[state=checked]:border-sidebar"
                        />
                      </FormControl>
                      <label className="text-sm font-medium">Team Lead</label>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>

          {/* Additional Details */}
          <div className="mt-3">
            <h4>Additional Details</h4>
            <div className="mt-3 grid grid-cols-2 gap-x-2 gap-y-4">
              <div className="col-span-2">
                <FormField
                  control={control}
                  name="professional_image"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <FileUploadField
                          name="professional_image"
                          label="Professional Image"
                          isPicture={true}
                          value={field.value}
                          onChange={field.onChange}
                          error={
                            errors.professional_image?.message
                              ? String(errors.professional_image.message)
                              : undefined
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="col-span-2">
                <FormField
                  control={control}
                  name="bio"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bio</FormLabel>
                      <FormControl>
                        <textarea
                          placeholder="Bio"
                          className="w-full h-24 p-2 border rounded-md resize-none"
                          {...field}
                          value={field.value || ""}
                          onChange={(e) =>
                            field.onChange(e.target.value || null)
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={control}
                name="preferred_notification_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Alternate Phone Number</FormLabel>
                    <FormControl>
                      <PhoneNumberInput
                        value={{
                          iso: form.watch(
                            "preferred_notification_country_iso",
                          ) as CountryCode,
                          national: field.value || "",
                        }}
                        onChange={({ iso, national }) => {
                          form.setValue(
                            "preferred_notification_country_iso",
                            iso,
                            { shouldValidate: true },
                          );
                          field.onChange(national || null);
                        }}
                        onBlur={field.onBlur}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {supervisors?.length > 0 && (
                <FormField
                  control={control}
                  name="supervisor_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Select Supervisor</FormLabel>
                      <Select
                        value={field.value || ""}
                        onValueChange={field.onChange}
                      >
                        <FormControl>
                          <SelectTrigger className="h-12">
                            <SelectValue placeholder="Select Supervisor" />
                            {/* Select a supervisor */}
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {supervisors.map((supervisor) => (
                            <SelectItem
                              key={supervisor.id}
                              value={supervisor.id}
                            >
                              {supervisor.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {leads?.length > 0 && (
                <FormField
                  control={control}
                  name="lead_ids"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Assign Team Leads</FormLabel>
                      <FormControl>
                        <ProfessionalLeadsSelector
                          value={field.value || []}
                          onChange={field.onChange}
                          options={leads.map((lead) => ({
                            id: lead.id,
                            name: lead.name,
                          }))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
          </div>

          {/* Age Ranges */}
          <div className="mt-3">
            <h4>Age Ranges</h4>
            <div className="mt-3 flex justify-between">
              <div>
                <FormField
                  control={control}
                  name="age_range_minor"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Minor Age Range</FormLabel>
                      <div className="flex gap-2">
                        <FormControl>
                          <Input
                            type="number"
                            className="shadow-none drop-shadow-none h-12 w-[90px]"
                            value={field.value?.[0] || 0}
                            onChange={(e) => {
                              const value = parseInt(e.target.value);
                              field.onChange([value, field.value?.[1] || 17]);
                            }}
                            min={0}
                            max={17}
                          />
                        </FormControl>
                        <span className="flex items-center mx-2">to</span>
                        <FormControl>
                          <Input
                            type="number"
                            className="shadow-none drop-shadow-none h-12 w-[90px]"
                            value={field.value?.[1] || 17}
                            onChange={(e) => {
                              const value = parseInt(e.target.value);
                              field.onChange([field.value?.[0] || 0, value]);
                            }}
                            min={0}
                            max={17}
                          />
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div>
                <FormField
                  control={control}
                  name="age_range_adult"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Adult Age Range</FormLabel>
                      <div className="flex gap-2">
                        <FormControl>
                          <Input
                            type="number"
                            className="shadow-none drop-shadow-none h-12 w-[90px]"
                            value={field.value?.[0] || 18}
                            onChange={(e) => {
                              const value = parseInt(e.target.value);
                              field.onChange([value, field.value?.[1] || 100]);
                            }}
                            min={18}
                            max={100}
                          />
                        </FormControl>
                        <span className="flex items-center mx-2">to</span>
                        <FormControl>
                          <Input
                            type="number"
                            className="shadow-none drop-shadow-none h-12 w-[90px]"
                            value={field.value?.[1] || 100}
                            onChange={(e) => {
                              const value = parseInt(e.target.value);
                              field.onChange([field.value?.[0] || 18, value]);
                            }}
                            min={18}
                            max={100}
                          />
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>

          {/* Required Documents */}
          {/* Temporary : only showing upload images in update professionals */}
          {/* Later : same approach for update professionals */}
          {editMode && (
            <div className="mt-3">
              <h4>Required Documents</h4>{" "}
              <FormLabel className="text-sm text-gray-500 mt-1">
                Please upload high-resolution images for all documents.
              </FormLabel>
              <div className="mt-3 grid grid-cols-2 gap-x-2 gap-y-4">
                <FormField
                  control={control}
                  name="picture"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormControl>
                        <FileUploadField
                          name="picture"
                          label="Picture"
                          isPicture={true}
                          value={field.value}
                          onChange={field.onChange}
                          error={
                            errors.picture?.message
                              ? String(errors.picture.message)
                              : undefined
                          }
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="signature"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormControl>
                        <FileUploadField
                          name="signature"
                          label="Signature"
                          value={field.value}
                          onChange={field.onChange}
                          error={
                            errors.signature?.message
                              ? String(errors.signature.message)
                              : undefined
                          }
                          currentUrl={initialData?.signature ?? undefined}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="stamp"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormControl>
                        <FileUploadField
                          name="stamp"
                          label="Stamp"
                          value={field.value}
                          onChange={field.onChange}
                          error={
                            errors.stamp?.message
                              ? String(errors.stamp.message)
                              : undefined
                          }
                          currentUrl={initialData?.stamp ?? undefined}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="stamp_and_signature"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormControl>
                        <FileUploadField
                          name="stamp_and_signature"
                          label="Stamp And Signature"
                          value={field.value}
                          onChange={field.onChange}
                          error={
                            errors.stamp_and_signature?.message
                              ? String(errors.stamp_and_signature.message)
                              : undefined
                          }
                          currentUrl={initialData?.stamp_and_signature ?? undefined}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>
          )}
          </fieldset>

          {/* CTA — Delete / feature flags / save are edits; a viewer only gets Close */}
          <div className="flex items-end justify-end gap-x-4 mt-20">
            {readOnly ? (
              <Button type="button" variant="outline" onClick={onClose}>
                Close
              </Button>
            ) : (
              <>
            {editMode && onDelete && (
              <Button
                type="button"
                className="bg-red-500 w-32 h-10 hover:bg-red-500/80"
                onClick={() => initialData?.id && onDelete(initialData.id)}
              >
                Delete
              </Button>
            )}
            {editMode && initialData?.id && (
              <Button
                type="button"
                className="bg-blue-600 w-auto px-4 h-10 hover:bg-blue-700 text-white"
                onClick={() => {
                  toast({
                    title: "Updating feature flags...",
                    description: "Please wait, this may take a few seconds.",
                  });
                  updateFeatureFlags.mutate(initialData.id);
                }}
                disabled={updateFeatureFlags.isPending}
              >
                {updateFeatureFlags.isPending
                  ? "Updating..."
                  : "Update Feature Flags"}
              </Button>
            )}
            <Button
              type="submit"
              className="bg-sidebar text-white w-32 h-10 hover:bg-sidebar"
              disabled={
                createProfessional.isPending ||
                updateProfessional.isPending ||
                isSubmitting
              }
            >
              {createProfessional.isPending || updateProfessional.isPending
                ? "Saving..."
                : editMode
                ? "Update"
                : "Add"}
            </Button>
              </>
            )}
          </div>
        </div>
      </form>
    </Form>
  );
}
